<template>
  <section
    class="py-20 w-full mx-auto flex flex-col gap-10 bg-gradient-to-t from-[#F2F9FF] to-white"
  >
    <div class="text-center text-4xl leading-[48px] font-semibold">
      <template v-if="!useAltTitle">
        {{ t('General.pricing.title') }}
      </template>
      <template v-else>
        <div class="text-xl leading-6">{{ t('General.pricing.altTitle') }}</div>
        <div class="text-sm leading-6 font-normal text-grey-90">
          {{ t('General.pricing.altSubtitle') }}
        </div>
      </template>
    </div>

    <div
      class="flex gap-3 custom-padding max-w-[1280px] mx-auto leading-normal flex-wrap justify-center"
    >
      <div
        v-for="p in plans"
        :key="p.tier"
        :class="{ 'border-eureka-500': p.tier === 'PRO' }"
        class="plan-tier-card flex flex-col gap-6 bg-white rounded-[20px] p-4 pt-5 border border-[#DDEBF8] relative"
      >
        <div
          v-if="p.tier === 'PRO'"
          class="top-4 right-2 absolute bg-grey-20 rounded-[40px] p-1 gap-2 flex"
        >
          <button
            class="rounded-[20px] px-2 py-1 text-xs transition-all"
            :class="{ [activeBadgeClass]: 'MONTHLY' === proPeriod }"
            @click="proPeriod = 'MONTHLY'"
          >
            {{ p.badge?.monthly }}
          </button>
          <button
            class="rounded-[20px] px-2 py-1 text-xs transition-all"
            :class="{ [activeBadgeClass]: 'YEARLY' === proPeriod }"
            @click="proPeriod = 'YEARLY'"
          >
            <span>{{ p.badge?.yearly }}</span>
            <span class="ml-2 text-[#008236]">{{ t('General.pricing.savings') }}</span>
          </button>
        </div>

        <div
          v-if="p.tier === 'PRO'"
          class="absolute top-[-10px] flex justify-center items-center w-full left-0"
        >
          <div class="bg-eureka-500 text-white px-2 py-0.5 rounded-[20px] text-xs">
            {{ t('General.pricing.mostPopular') }}
          </div>
        </div>

        <div class="gap-2 flex flex-col">
          <span class="h-12 font-semibold text-[20px] leading-[28px] tracking-normal">
            {{ p.name }}
          </span>
          <div class="h-[84px]">
            <span class="font-semibold text-[36px] leading-[48px] tracking-normal">
              <template v-if="p.tier !== 'PRO'">
                {{ p.price }}
              </template>
              <template v-else>
                <template v-if="proPeriod === 'MONTHLY'">
                  {{ p.price }}
                </template>
                <template v-else>
                  {{ t('General.pricing.yearlyPrice') }}
                </template>
              </template>
            </span>
            <span v-if="'period' in p" class="font-normal text-sm leading-5 text-grey-90 ml-1">
              {{ `${p.period}` }}
            </span>
          </div>
          <span class="h-[70px] font-normal text-sm leading-5 tracking-normal text-grey-450">
            {{ p.desc }}
          </span>
        </div>
        <button
          class="py-2 px-[28px] rounded-[20px] text-base"
          :class="getCtaClass(p.tier)"
          @click="emits('cta', p.tier, proPeriod)"
        >
          <span
            v-if="isPaymentLoading && p.tier === 'PRO'"
            class="th-loading th-loading-spinner th-loading-xs"
          ></span>
          <span v-else>{{ p.cta }}</span>
        </button>
        <div v-for="(f, i) of p.features" :key="i" class="flex flex-col gap-2">
          <span v-if="'header' in f" class="leading-6 font-semibold">
            {{ f.header }}
          </span>
          <div v-for="(item, j) of f.items" :key="j" class="gap-1 flex text-grey-450 text-sm">
            <PtIcon :icon="circleRightIcon" class="w-6 h-4 text-eureka-500 flex-shrink-0 mt-0.5" />
            <span>{{ item }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz';
import { setupI18n } from '@eureka/shared/locales/i18n';
import circleRightIcon from '@/assets/icon/circle-right-icon.svg';
import { computed, ref } from 'vue';
import { useJpUser } from '@/composables/utils/use-jp-user';

const { isJapaneseUserInTargetTimezone } = useJpUser();

withDefaults(
  defineProps<{
    isPaymentLoading?: boolean;
    useAltTitle?: boolean;
  }>(),
  {
    isPaymentLoading: false,
    useAltTitle: false,
  },
);

const emits = defineEmits(['cta']);

const { t } = setupI18n();

const proPeriod = ref<'MONTHLY' | 'YEARLY'>('MONTHLY');

const activeBadgeClass = 'bg-white shadow-[0px_0px_4px_0px_#0764E924,0px_4px_8px_0px_#0764E914]';

const plans = computed(() => {
  return [
    {
      tier: 'BASIC',
      name: t('General.pricing.basic.name'),
      price: t('General.pricing.basic.price'),
      desc: t('General.pricing.basic.description'),
      cta: t('General.pricing.basic.cta'),
      features: [
        {
          items: [
            t('General.pricing.basic.fi1'),
            t('General.pricing.basic.fi2'),
            t('General.pricing.basic.fi3'),
          ],
        },
      ],
    },
    {
      tier: 'PRO',
      name: t('General.pricing.pro.name'),
      price: t('General.pricing.pro.price'),
      period: t('General.pricing.pro.period'),
      desc: t('General.pricing.pro.description'),
      cta: t('General.pricing.pro.cta'),
      badge: {
        monthly: t('General.pricing.pro.badge.monthly'),
        yearly: t('General.pricing.pro.badge.yearly'),
      },
      features: [
        {
          header: t('General.pricing.pro.fh1'),
          items: [
            t('General.pricing.pro.fi1'),
            t('General.pricing.pro.fi2'),
            t('General.pricing.pro.fi3'),
            t('General.pricing.pro.fi4'),
          ],
        },
      ],
    },
    {
      tier: 'ENTERPRISE',
      name: t('General.pricing.enterprise.name'),
      price: t('General.pricing.enterprise.price'),
      desc: t('General.pricing.enterprise.description'),
      cta: t('General.pricing.enterprise.cta'),
      includes: t('General.pricing.enterprise.includes'),
      features: [
        {
          header: t('General.pricing.enterprise.fh1'),
          items: [t('General.pricing.enterprise.fi1'), t('General.pricing.enterprise.fi2')],
        },
        {
          header: t('General.pricing.enterprise.fh2'),
          items: [
            t('General.pricing.enterprise.fi3'),
            t('General.pricing.enterprise.fi4'),
            t('General.pricing.enterprise.fi5'),
            t('General.pricing.enterprise.fi6'),
          ],
        },
        {
          header: t('General.pricing.enterprise.fh3'),
          items: [
            t('General.pricing.enterprise.fi7'),
            t('General.pricing.enterprise.fi8'),
            t('General.pricing.enterprise.fi9'),
            t('General.pricing.enterprise.fi10'),
          ],
        },
      ],
    },
    {
      tier: 'API',
      name: t('General.pricing.api.name'),
      price: t('General.pricing.api.price'),
      desc: t('General.pricing.api.description'),
      cta: t('General.pricing.api.cta'),
      includes: t('General.pricing.api.includes'),
      features: [
        {
          items: [
            t('General.pricing.api.fi1'),
            t('General.pricing.api.fi2'),
            t('General.pricing.api.fi3'),
            t('General.pricing.api.fi4'),
          ],
        },
      ],
    },
  ].filter((plan) => {
    return plan.tier !== 'PRO' || !isJapaneseUserInTargetTimezone.value;
  });
});

const getCtaClass = (tier: string) => {
  if (tier === 'PRO') {
    return 'bg-eureka-500 text-white';
  }
  if (tier === 'BASIC') {
    return 'bg-grey-30 cursor-not-allowed';
  }
  return 'bg-[#020A1A] text-white';
};
</script>

<style lang="scss" scoped>
.custom-padding {
  @apply px-20;
}

.plan-tier-card {
  width: calc(25% - 12px); // 4 per row, gap-3
}

@media (max-width: 768px) {
  .custom-padding {
    @apply px-10;
  }

  .plan-tier-card {
    width: calc(50% - 12px); // 2 per row, gap-3
  }
}

@media (max-width: 375px) {
  .custom-padding {
    @apply px-5;
  }

  .plan-tier-card {
    width: 100%;
  }
}
</style>
